import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import axios from 'axios';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import translate from '../../../commonComponents/translate';
import {
  compose,
  formatDate,
  PROFILE_BLOCK,
  PROFILE_BLOCKS,
  PROFILE_TAB,
} from '../../../services/utilityFunctions';
import UserProp from '../../../commonComponents/propTypes/userProp';
import SettingsIcon from './icons/SettingsIcon';
import PaymentIcon from './icons/PaymentIcon';
import ServicesContext from '../../../contextProviders/ServicesContext';
import Avatar from './Avatar';
import Notifications from '../../../services/notifications';
import withCSRF from '../../../commonComponents/withCSRF';
import { useModal } from '../../../services/customHooks';
import ShareModal from '../../academy/modal/ShareModal';
import LevelCard from './components/LevelCard';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import { PAYMENT_PROVIDER } from '../../../commonComponents/propTypes/tenantProp';
import ProfileBlockIcon from './icons/ProfileBlockIcon';
import UploadIcon from '../../../commonComponents/icons/icon/UploadIcon';
import SocialMediaIcon from './icons/SocialMediaIcon';
import BookmarkIcon from './icons/BookmarkIcon';

const bColor = p => {
  if (p >= 0 && p < 33) {
    return 'danger';
  }

  if (p >= 33 && p < 66) {
    return 'warning';
  }

  return 'success';
};

function SidePanel({
  t,
  member,
  activeTab,
  onTabSelection,
  _csrf,
  directSellingActive,
  isMarketPlaceEnabled,
}) {
  const [cookies] = useCookies(['isTestMode']);
  const isTestMode =
    cookies.isTestMode === 'true' || cookies.isTestMode === true;

  const { tenant, organization } = useContext(ConfigsContext);
  const { calendly } = organization?.services || {};
  // we only have calendly for now
  const shouldShowConnectors =
    calendly?.config?.configured && calendly?.enabled;
  const { paymentProviderType, distributorPageAvailableToAllTypes } =
    tenant?.settings || {};
  const services = useContext(ServicesContext);
  const [user, setUser] = useState(member);
  const disabledBlocks =
    services && services.me && services.me.config && services.me.config.disabled
      ? services.me.config.disabled
      : [];
  const connectIsAvailable =
    services && services.connect && services.connect.enabled === true;
  const performIsAvailable =
    services && services.perform && services.perform.enabled === true;
  const academyIsAvailable =
    services && services.academy && services.academy.enabled === true;
  const certificatesIsAvailable =
    services && services.certificates && services.certificates.enabled === true;
  const posterIsAvailable =
    services && services.poster && services.poster.enabled === true;
  const shareIsAvailable =
    distributorPageAvailableToAllTypes || member.type === 'collaborator';

  const progress = member.progressByBlock || {};
  const disableSharePage =
    !member?.userPageName || member?.userPageName?.trim().length === 0;

  const handleImage = value => {
    axios
      .put(
        '/profile/edit',
        { image: value },
        {
          headers: {
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(({ data }) => {
        setUser(data.data);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err);
        console.log('Err', err);
      });
  };

  /** Share Page Modal */
  const [sharePageModal, showSharePageModal] = useModal({ show: false });

  const hasLevel = member?.level !== null || member?.forecast?.level !== null;

  return (
    <>
      {sharePageModal.show && (
        <ShareModal
          url={member?.userPageLink || ''}
          userPageName={member?.userPageName}
          onClose={() => {
            showSharePageModal({ show: false });
          }}
          t={t}
          isShareUserPage
          isMarketPlaceEnabled={isMarketPlaceEnabled}
        />
      )}
      <div className="m-portlet m-portlet--full-height">
        <div className="m-portlet__body">
          <div className="card-profile">
            <div className="profile-head">
              <div className="profile-media">
                <Avatar
                  onChange={handleImage}
                  url={user.image ? user.image.thumb : '/images/no_user.jpg'}
                />
              </div>
              <div className="profile-content">
                <div className="profile-section">
                  <span className="profile-username">{member.username}</span>
                  <span className="profile-subtitle">
                    {member.position || member.title}
                  </span>
                </div>
                <div className="profile-action">
                  {(connectIsAvailable ||
                    performIsAvailable ||
                    academyIsAvailable ||
                    certificatesIsAvailable) && (
                    <button
                      onClick={() => onTabSelection(PROFILE_TAB.PERFORMANCE)}
                      type="button"
                      className="btn btn-info btn-sm"
                    >
                      {t('profile_tab_performance')}
                    </button>
                  )}
                  {posterIsAvailable && (
                    <button
                      onClick={() => onTabSelection(PROFILE_TAB.POSTER)}
                      type="button"
                      className="btn btn-success btn-sm"
                    >
                      {t('profile_tab_poster')}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="profile-body">
              <div className="profile-content">
                {member.isTrainer && (
                  <div className="">
                    <h4 className="">{`${t('profile_trainer.profile')}`}</h4>
                    <hr />
                  </div>
                )}
                {member.userNumber && (
                  <div className="profile-info">
                    <div className="label">{`${t(
                      'profile.userNumber',
                    )}: `}</div>
                    <div className="data">
                      {`${member.userNumber}`.padStart(5, '0')}
                    </div>
                  </div>
                )}
                <div className="profile-info">
                  <div className="label">{`${t('member_since')}: `}</div>
                  <div className="data">
                    {moment(member.createdAt).format('DD MMM YYYY')}
                  </div>
                </div>
                <div className="profile-info">
                  <div className="label">{`${t('profile_email')}: `}</div>
                  <div className="data">{member.email}</div>
                </div>
                <div className="profile-info">
                  <div className="label">{`${t('profile_birthday')}: `}</div>
                  <div className="data">
                    {member.birthday ? formatDate(member.birthday) : t('n/a')}
                  </div>
                </div>
                {member.rating && (
                  <div className="profile-info">
                    <div className="label">
                      {`${member.rating.total || 0} ${t('ratings')}`}
                    </div>
                    <div className="data">
                      {Array.from({ length: member.rating.value || 0 }).map(
                        (value, index) => (
                          <i
                            key={index.toString()}
                            className="fa fa-star"
                            style={{ color: 'var(--yellow)' }}
                          />
                        ),
                      )}
                    </div>
                  </div>
                )}
                {hasLevel && (
                  <div className="level-card-container">
                    <LevelCard
                      user={member}
                      directSellingActive={directSellingActive}
                    />
                  </div>
                )}

                {shareIsAvailable && !isTestMode && (
                  <button
                    onClick={() => showSharePageModal({ show: true })}
                    type="button"
                    disabled={disableSharePage}
                    className="btn btn-dynamic btn-sm"
                  >
                    {t('profile.share.userPage')}
                  </button>
                )}
              </div>
              <div className="profile-items">
                {PROFILE_BLOCKS.map(block => {
                  const isAvailability = block === PROFILE_BLOCK.AVAILABILITY;
                  if (isAvailability && !member.isTrainer) {
                    return null;
                  }
                  if (disabledBlocks.includes(block)) {
                    return null;
                  }
                  const bProgress = progress[block] || 0;
                  const color = bColor(bProgress);
                  return (
                    <a
                      key={block}
                      onClick={() => onTabSelection(block)}
                      className={cx({
                        'profile-item': true,
                        active: activeTab === block,
                      })}
                    >
                      <span className="profile-section">
                        <span className="icon">
                          <ProfileBlockIcon block={block} />
                        </span>
                        <span className="desc">
                          {t(`profile_tab_${block}`)}
                        </span>
                      </span>
                      {!isAvailability && (
                        <span
                          className={`m-badge m-badge--wide m-badge--rounded m-badge--${color}`}
                          style={{ width: '3.5rem' }}
                        >
                          {`${Math.round(bProgress)}%`}
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
              <div className="profile-items-separator" />
              <div className="profile-items">
                <a
                  onClick={() => onTabSelection(PROFILE_TAB.FOLLOWING)}
                  className={cx({
                    'profile-item': true,
                    active: activeTab === PROFILE_TAB.FOLLOWING,
                  })}
                >
                  <span className="profile-section">
                    <span className="icon">
                      <BookmarkIcon width={19} height={22} color="#575962" />
                    </span>
                    <span className="desc">{t('following')}</span>
                  </span>
                </a>

                <a
                  onClick={() => onTabSelection(PROFILE_TAB.SIGN_BY_ME)}
                  className={cx({
                    'profile-item': true,
                    active: activeTab === PROFILE_TAB.SIGN_BY_ME,
                  })}
                >
                  <span className="profile-section">
                    <span className="icon">
                      <UploadIcon width={19} height={22} color="#575962" />
                    </span>
                    <span className="desc">{t('uploaded.documents')}</span>
                  </span>
                </a>
                {(paymentProviderType === PAYMENT_PROVIDER.STRIPE ||
                  paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) && (
                  <a
                    onClick={() => onTabSelection(PROFILE_TAB.PAYMENT)}
                    className={cx({
                      'profile-item': true,
                      active: activeTab === PROFILE_TAB.PAYMENT,
                    })}
                  >
                    <span className="profile-section">
                      <span className="icon">
                        <PaymentIcon />
                      </span>
                      <span className="desc">{t('profile_tab_payment')}</span>
                    </span>
                  </a>
                )}
                <a
                  onClick={() => onTabSelection(PROFILE_TAB.SETTINGS)}
                  className={cx({
                    'profile-item': true,
                    active: activeTab === PROFILE_TAB.SETTINGS,
                  })}
                >
                  <span className="profile-section">
                    <span className="icon">
                      <SettingsIcon />
                    </span>
                    <span className="desc">{t('profile_tab_settings')}</span>
                  </span>
                </a>
                {shouldShowConnectors && (
                  <a
                    onClick={() => onTabSelection(PROFILE_TAB.CONNECTORS)}
                    className={cx({
                      'profile-item': true,
                      active: activeTab === PROFILE_TAB.CONNECTORS,
                    })}
                  >
                    <span className="profile-section">
                      <span className="icon">
                        <SocialMediaIcon />
                      </span>
                      <span className="desc">
                        {t('profile_tab_connectors')}
                      </span>
                    </span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

SidePanel.propTypes = {
  t: PropTypes.func.isRequired,
  member: PropTypes.shape(UserProp.propType).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabSelection: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  isMarketPlaceEnabled: PropTypes.bool,
};
SidePanel.defaultProps = {
  isMarketPlaceEnabled: false,
};

export default compose(translate, withCSRF)(SidePanel);
